import * as React from "react";
import {
    Modal,
    Button,
    Segment,
    Header,
    Icon,
    Dimmer,
    Loader,
    Message,
    Dropdown,
    Label,
    Table,
    Checkbox,
    Grid,
    Menu,
    Input
} from "semantic-ui-react";
import AceEditor from "react-ace";
import "ace-builds/src-noconflict/mode-json";
import "ace-builds/src-noconflict/theme-monokai";
import { TransportOrderSelector } from "./TransportOrderSelector";
import { TransportOrderJsonViewer } from "./TransportOrderJsonViewer";
import { backend } from "../../../../../xconvert-backend";
import { authentication } from "../../../../../authentication";
import { notify } from "react-notify-toast";

interface ScreenshotDifference {
    feldname: string;
    istWert?: string;
    sollWert: string;
    comment?: string;
    isFeldMentionedByClient: boolean;
    selected?: boolean;
    hidden?: boolean;
}

interface ScreenshotComparisonResponse {
    differences: ScreenshotDifference[] | null;
    databaseEntryId: string | null;
    totalUsageTokensNumber: number;
    totalCompletionTokensNumber: number;
    totalPrice: number;
}

interface ScreenshotDifferenceWithJsonField {
    feldname: string;
    fieldNameInTransportOrderJson: string | null;
    istWert?: string;
    sollWert: string;
    comment?: string;
    isFeldMentionedByClient: boolean;
    selected?: boolean;
    hidden?: boolean;
}

interface ScreenshotDifferenceJsonMappingResponse {
    differences: ScreenshotDifferenceWithJsonField[] | null;
    databaseEntryId: string | null;
    totalUsageTokensNumber: number;
    totalCompletionTokensNumber: number;
    totalPrice: number;
}

interface ScreenshotComparisonModalProps {
    isOpen: boolean;
    onClose: () => void;
    onApply: (text: string) => void;
    transportOrderIds: string[];
    onTransportOrderIdsChange: (ids: string[]) => void;
}

type ImageProcessingMode = 'VISION_MODEL' | 'GOOGLE_OCR';
type ScreenshotComparisonMode = 'IST_VS_SOLL' | 'SOLL_ONLY';
type ViewMode = 'table' | 'json' | 'split';

const ALLOWED_MODELS = [
    { key: 'CLAUDE45_SONNET', text: 'Claude 4.5 Sonnet', value: 'CLAUDE45_SONNET' },
    { key: 'CLAUDE4_SONNET', text: 'Claude 4 Sonnet', value: 'CLAUDE4_SONNET' },
    { key: 'CLAUDE45_HAIKU', text: 'Claude 4.5 Haiku', value: 'CLAUDE45_HAIKU' },
    { key: 'CLAUDE45_OPUS', text: 'Claude 4.5 Opus', value: 'CLAUDE45_OPUS' }
];

const IMAGE_PROCESSING_MODES = [
    { key: 'VISION_MODEL', text: 'Vision Model (Claude)', value: 'VISION_MODEL' },
    { key: 'GOOGLE_OCR', text: 'Google OCR (Logenios)', value: 'GOOGLE_OCR' }
];

const COMPARISON_MODES = [
    { key: 'IST_VS_SOLL', text: 'IST vs SOLL (2 images)', value: 'IST_VS_SOLL' },
    { key: 'SOLL_ONLY', text: 'SOLL Only (1 image)', value: 'SOLL_ONLY' }
];

const isEnrichedDifference = (
    diff: ScreenshotDifference | ScreenshotDifferenceWithJsonField
): diff is ScreenshotDifferenceWithJsonField => {
    return 'fieldNameInTransportOrderJson' in diff;
};

const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve((reader.result as string).split(',')[1]);
        reader.onerror = reject;
    });
};

const normalizeValue = (value?: string): string => {
    if (!value) return '';
    return String(value).trim().toLowerCase();
};

const sanitizeString = (value: any): string => {
    if (value === null || value === undefined) return '';
    if (typeof value === 'boolean') {
        return value ? 'vorhanden' : 'nicht vorhanden';
    }
    return String(value);
};

const areValuesEqual = (istWert: string | undefined, sollWert: string): boolean => {
    return normalizeValue(istWert) === normalizeValue(sollWert);
};

export function ScreenshotComparisonModal({
                                              isOpen,
                                              onClose,
                                              onApply,
                                              transportOrderIds,
                                              onTransportOrderIdsChange
                                          }: ScreenshotComparisonModalProps) {


    const [istImage, setIstImage] = React.useState<{ file: File; preview: string } | null>(null);
    const [sollImage, setSollImage] = React.useState<{ file: File; preview: string } | null>(null);
    const [istZoom, setIstZoom] = React.useState<number>(1);
    const [sollZoom, setSollZoom] = React.useState<number>(1);

    const [comparisonMode, setComparisonMode] = React.useState<ScreenshotComparisonMode>('IST_VS_SOLL');
    const [selectedModel, setSelectedModel] = React.useState<string>('CLAUDE45_SONNET');
    const [imageProcessingMode, setImageProcessingMode] = React.useState<ImageProcessingMode>('VISION_MODEL');
    const [customPrompt, setCustomPrompt] = React.useState<string>('');

    // Processing state
    const [isProcessing, setIsProcessing] = React.useState<boolean>(false);
    const [isEnriching, setIsEnriching] = React.useState<boolean>(false);
    const [error, setError] = React.useState<string>('');

    const [response, setResponse] = React.useState<ScreenshotComparisonResponse | null>(null);
    const [differences, setDifferences] = React.useState<ScreenshotDifference[]>([]);
    const [enrichedDifferences, setEnrichedDifferences] = React.useState<ScreenshotDifferenceWithJsonField[]>([]);
    const [hasEnriched, setHasEnriched] = React.useState<boolean>(false);
    const [enrichmentResponse, setEnrichmentResponse] = React.useState<ScreenshotDifferenceJsonMappingResponse | null>(null);

    const [viewMode, setViewMode] = React.useState<ViewMode>('table');
    const [isTableExpanded, setIsTableExpanded] = React.useState<boolean>(false);
    const [showEnrichedTable, setShowEnrichedTable] = React.useState<boolean>(true);
    const [hideMatchingFields, setHideMatchingFields] = React.useState<boolean>(false);
    const [hideUnmappedFields, setHideUnmappedFields] = React.useState<boolean>(false);

    // Transport Order state
    const [transportOrderJson, setTransportOrderJson] = React.useState<string>('');
    const [optimizedTransportOrderJson, setOptimizedTransportOrderJson] = React.useState<string>('');
    const [transportOrderSelectorOpen, setTransportOrderSelectorOpen] = React.useState<boolean>(false);
    const [isLoadingTransportOrder, setIsLoadingTransportOrder] = React.useState<boolean>(false);
    const [showTransportOrderConfig, setShowTransportOrderConfig] = React.useState<boolean>(false);

    const [isDraggingIst, setIsDraggingIst] = React.useState<boolean>(false);
    const [isDraggingSoll, setIsDraggingSoll] = React.useState<boolean>(false);

    // Refs
    const istInputRef = React.useRef<HTMLInputElement>(null);
    const sollInputRef = React.useRef<HTMLInputElement>(null);
    const bothInputRef = React.useRef<HTMLInputElement>(null);

    React.useEffect(() => {
        if (transportOrderIds.length === 1) {
            loadTransportOrderJson(transportOrderIds[0]);
        } else {
            setTransportOrderJson('');
            setOptimizedTransportOrderJson('');
            if (transportOrderIds.length === 0) {
                setShowTransportOrderConfig(false);
            }
        }
    }, [transportOrderIds]);

    React.useEffect(() => {
        if (!isOpen) {
            if (istImage) URL.revokeObjectURL(istImage.preview);
            if (sollImage) URL.revokeObjectURL(sollImage.preview);
            resetState();
        }
    }, [isOpen]);

    const resetState = () => {
        setIstImage(null);
        setSollImage(null);
        setCustomPrompt('');
        setError('');
        setResponse(null);
        setDifferences([]);
        setIstZoom(1);
        setSollZoom(1);
        setViewMode('table');
        setIsTableExpanded(false);
        setEnrichedDifferences([]);
        setHasEnriched(false);
        setEnrichmentResponse(null);
        setShowTransportOrderConfig(false);
        setShowEnrichedTable(true);
        setHideMatchingFields(false);
        setHideUnmappedFields(false);
        setComparisonMode('IST_VS_SOLL');
    };

    const loadTransportOrderJson = async (transportOrderId: string) => {
        setIsLoadingTransportOrder(true);
        try {
            const auth = backend.withTokenAuthHeader(authentication.token);
            const transportOrder = await backend.transportOrderApi.fetchTransportOrderById(transportOrderId, auth);
            const jsonString = JSON.stringify(transportOrder, null, 2);
            setTransportOrderJson(jsonString);
            setOptimizedTransportOrderJson(jsonString);
        } catch (error) {
            console.error("Failed to load transport order:", error);
            notify.show('Failed to load transport order', 'error', 3000);
            setTransportOrderJson('');
            setOptimizedTransportOrderJson('');
        } finally {
            setIsLoadingTransportOrder(false);
        }
    };

    const handleOptimizedJsonChange = (newJson: string) => {
        setOptimizedTransportOrderJson(newJson);
    };

    const getVisibleData = () => {
        let dataToUse: (ScreenshotDifference | ScreenshotDifferenceWithJsonField)[] =
            (hasEnriched && showEnrichedTable) ? enrichedDifferences : differences;

        if (hideMatchingFields) {
            if (comparisonMode === 'SOLL_ONLY') {
                // In Soll Only, "hide matching" becomes "hide empty soll values"
                dataToUse = dataToUse.filter(d => d.sollWert && d.sollWert.trim() !== '');
            } else {
                dataToUse = dataToUse.filter(d => !areValuesEqual(d.istWert, d.sollWert));
            }
        }

        if (hasEnriched && showEnrichedTable && hideUnmappedFields) {
            dataToUse = (dataToUse as ScreenshotDifferenceWithJsonField[]).filter(d =>
                d.fieldNameInTransportOrderJson !== null &&
                d.fieldNameInTransportOrderJson !== undefined &&
                d.fieldNameInTransportOrderJson !== ''
            );
        }

        return dataToUse;
    };

    const visibleData = getVisibleData();
    const selectedData = visibleData.filter(d => d.selected && !d.hidden);
    const selectedCount = selectedData.length;

    const mappedCount = enrichedDifferences.filter(d =>
        d.fieldNameInTransportOrderJson !== null &&
        d.fieldNameInTransportOrderJson !== undefined &&
        d.fieldNameInTransportOrderJson !== ''
    ).length;

    const unmappedCount = enrichedDifferences.length - mappedCount;
    const hasResults = differences.length > 0;

    const matchingFieldsCount = (hasEnriched && showEnrichedTable ? enrichedDifferences : differences)
        .filter(d => {
            if (comparisonMode === 'SOLL_ONLY') {
                return !d.sollWert || d.sollWert.trim() === '';
            }
            return areValuesEqual(d.istWert, d.sollWert);
        }).length;

    const handleSingleFileSelect = (file: File, type: 'ist' | 'soll') => {
        if (!file.type.startsWith('image/')) {
            setError('Please upload an image file (JPEG or PNG)');
            return;
        }

        const preview = URL.createObjectURL(file);
        const imageData = { file, preview };

        if (type === 'ist') {
            if (istImage) URL.revokeObjectURL(istImage.preview);
            setIstImage(imageData);
        } else {
            if (sollImage) URL.revokeObjectURL(sollImage.preview);
            setSollImage(imageData);
        }
        setError('');
    };

    const handleBothFilesSelect = (files: FileList) => {
        if (files.length > 2) {
            setError('Maximum 2 images allowed');
            return;
        }
        const fileArray = Array.from(files).filter(f => f.type.startsWith('image/'));

        if (fileArray.length === 0) {
            setError('Please upload image files (JPEG or PNG)');
            return;
        }

        if (fileArray.length >= 1) {
            if (istImage) URL.revokeObjectURL(istImage.preview);
            setIstImage({ file: fileArray[0], preview: URL.createObjectURL(fileArray[0]) });
        }

        if (fileArray.length === 2) {
            if (sollImage) URL.revokeObjectURL(sollImage.preview);
            setSollImage({ file: fileArray[1], preview: URL.createObjectURL(fileArray[1]) });
        }
        setError('');
    };

    const handlePaste = async (e: React.ClipboardEvent, type: 'ist' | 'soll') => {
        const items = e.clipboardData?.items;
        if (!items) return;

        for (let i = 0; i < items.length; i++) {
            if (items[i].type.startsWith('image/')) {
                const file = items[i].getAsFile();
                if (file) {
                    handleSingleFileSelect(file, type);
                    e.preventDefault();
                    return;
                }
            }
        }
    };

    const handleDrop = (e: React.DragEvent, type: 'ist' | 'soll') => {
        e.preventDefault();
        e.stopPropagation();
        if (type === 'ist') setIsDraggingIst(false);
        else setIsDraggingSoll(false);

        const files = e.dataTransfer?.files;
        if (files && files.length > 0) {
            const file = files[0];
            if (file.type.startsWith('image/')) {
                handleSingleFileSelect(file, type);
            } else {
                setError('Please drop an image file (JPEG or PNG)');
            }
        }
    };

    const handleDragOver = (e: React.DragEvent, type: 'ist' | 'soll') => {
        e.preventDefault();
        e.stopPropagation();
        if (type === 'ist') setIsDraggingIst(true);
        else setIsDraggingSoll(true);
    };

    const handleDragLeave = (e: React.DragEvent, type: 'ist' | 'soll') => {
        e.preventDefault();
        e.stopPropagation();
        if (type === 'ist') setIsDraggingIst(false);
        else setIsDraggingSoll(false);
    };

    const handleInvert = () => {
        const tempIst = istImage;
        const tempSoll = sollImage;
        const tempIstZoom = istZoom;
        const tempSollZoom = sollZoom;

        setIstImage(tempSoll);
        setSollImage(tempIst);
        setIstZoom(tempSollZoom);
        setSollZoom(tempIstZoom);
    };

    const handleCompare = async () => {
        if (comparisonMode === 'IST_VS_SOLL') {
            if (!istImage || !sollImage) {
                setError('Please upload both IST and SOLL images');
                return;
            }
        } else {
            if (!sollImage) {
                setError('Please upload SOLL image');
                return;
            }
        }

        setIsProcessing(true);
        setError('');
        setHasEnriched(false);
        setEnrichedDifferences([]);
        setEnrichmentResponse(null);

        try {
            const getExtensionFromFile = (file) => {
                const fileName = file.name;
                const lastDot = fileName.lastIndexOf('.');
                return lastDot !== -1 ? fileName.substring(lastDot) : '.png';
            };

            const requestBody: any = {
                mode: comparisonMode,
                sollScreenshot: {
                    fileName: `sollScreenshot${getExtensionFromFile(sollImage.file)}`,
                    base64Data: await fileToBase64(sollImage.file),
                    mediaType: sollImage.file.type
                },
                customAdditionalPrompt: customPrompt || null,
                model: selectedModel,
                imageProcessingMode: imageProcessingMode
            };

            if (comparisonMode === 'IST_VS_SOLL' && istImage) {
                requestBody.istScreenshot = {
                    fileName: `istScreenshot${getExtensionFromFile(istImage.file)}`,
                    base64Data: await fileToBase64(istImage.file),
                    mediaType: istImage.file.type
                };
            }

            const res = await fetch('/converter/v1/compareScreenshots', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(requestBody)
            });

            if (!res.ok) throw new Error(`Server error: ${res.status}`);

            const data: ScreenshotComparisonResponse = await res.json();
            setResponse(data);

            const diffsWithSelection = (data.differences || []).map(d => ({
                ...d,
                istWert: comparisonMode === 'SOLL_ONLY' ? undefined : sanitizeString(d.istWert),
                sollWert: sanitizeString(d.sollWert),
                comment: '', // Initialize empty comment
                selected: true,
                hidden: false
            }));
            setDifferences(diffsWithSelection);
        } catch (err: any) {
            setError(err.message || 'Failed to compare screenshots');
        } finally {
            setIsProcessing(false);
        }
    };

    const handleEnrichWithJsonMapping = async () => {
        if (!response || !response.differences || response.differences.length === 0) {
            setError('No differences to enrich');
            return;
        }

        if (!optimizedTransportOrderJson || optimizedTransportOrderJson.trim() === '') {
            setError('Please select a transport order');
            return;
        }

        try {
            JSON.parse(optimizedTransportOrderJson);
        } catch (e) {
            setError('Invalid transport order JSON format');
            return;
        }

        setIsEnriching(true);
        setError('');

        try {
            const differencesJson = JSON.stringify(differences.map(({ selected, hidden, comment, ...rest }) => rest));

            const requestBody = {
                differences: differencesJson,
                transportOrderJson: optimizedTransportOrderJson,
                model: selectedModel
            };

            const res = await fetch('/converter/v1/mapScreenshotDifferencesToJsonFields', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(requestBody)
            });

            if (!res.ok) {
                const errorData = await res.json().catch(() => ({ error: `Server error: ${res.status}` }));
                throw new Error(errorData.error || `Server error: ${res.status}`);
            }

            const data: ScreenshotDifferenceJsonMappingResponse = await res.json();
            setEnrichmentResponse(data);

            const enrichedDiffs = (data.differences || []).map(d => ({
                ...d,
                istWert: comparisonMode === 'SOLL_ONLY' ? undefined : sanitizeString(d.istWert),
                sollWert: sanitizeString(d.sollWert),
                comment: '',
                selected: true,
                hidden: false
            }));

            setEnrichedDifferences(enrichedDiffs);
            setHasEnriched(true);
            setShowEnrichedTable(true);

        } catch (err: any) {
            setError(err.message || 'Failed to enrich with JSON mapping');
        } finally {
            setIsEnriching(false);
        }
    };

    const handleApplyToPrompt = (format: 'raw' | 'json') => {
        if (selectedData.length === 0) {
            setError('No differences selected');
            return;
        }

        let text: string;
        if (format === 'json') {
            // Remove meta keys and handle removal of istWert if SOLL_ONLY
            const cleanedData = selectedData.map(({ selected, hidden, istWert, ...rest }) => {
                const obj: any = { ...rest };
                obj.comment = rest.comment || '';

                if (comparisonMode !== 'SOLL_ONLY') {
                    obj.istWert = istWert;
                }
                return obj;
            });
            text = JSON.stringify(cleanedData, null, 2);
        } else {
            // Raw Table Formatting
            const isEnriched = hasEnriched && showEnrichedTable;
            const isSollOnly = comparisonMode === 'SOLL_ONLY';

            // Define dynamic headers
            const headers = ["FIELD"];
            if (isEnriched) headers.push("JSON PATH");
            if (!isSollOnly) headers.push("IST VALUE");
            headers.push("SOLL VALUE");
            headers.push("COMMENT");

            const colWidths = new Array(headers.length).fill(20);

            selectedData.forEach(d => {
                let colIdx = 0;
                // Field
                colWidths[colIdx] = Math.max(colWidths[colIdx], (d.feldname || '').length);
                colIdx++;

                if (isEnriched) {
                    if (isEnrichedDifference(d)) {
                        colWidths[colIdx] = Math.max(colWidths[colIdx], (d.fieldNameInTransportOrderJson || '').length);
                    }
                    colIdx++;
                }

                if (!isSollOnly) {
                    colWidths[colIdx] = Math.max(colWidths[colIdx], (d.istWert || '').length);
                    colIdx++;
                }

                colWidths[colIdx] = Math.max(colWidths[colIdx], (d.sollWert || '').length);
                colIdx++;

                colWidths[colIdx] = Math.max(colWidths[colIdx], (d.comment || '').length);
            });

            colWidths.forEach((_, i) => colWidths[i] += 2);

            const pad = (str: string | null | undefined, width: number) => (str || '').padEnd(width);

            const tableHeader = headers.map((h, i) => pad(h, colWidths[i])).join(' | ');
            const separator = colWidths.map(w => '-'.repeat(w)).join('-+-');

            const rows = selectedData.map(d => {
                const parts: string[] = [];
                const name = d.isFeldMentionedByClient ? `${d.feldname} (*)` : d.feldname;

                parts.push(pad(name, colWidths[0]));

                let colIdx = 1;

                if (isEnriched) {
                    parts.push(pad(isEnrichedDifference(d) ? d.fieldNameInTransportOrderJson : '', colWidths[colIdx]));
                    colIdx++;
                }

                if (!isSollOnly) {
                    parts.push(pad(d.istWert, colWidths[colIdx]));
                    colIdx++;
                }

                parts.push(pad(d.sollWert, colWidths[colIdx]));
                colIdx++;

                parts.push(pad(d.comment, colWidths[colIdx]));

                return parts.join(' | ');
            });

            text = `COMPARISON RESULTS (${selectedData.length} items)\n\n${tableHeader}\n${separator}\n${rows.join('\n')}`;
        }

        onApply(text);
        onClose();
    };

    const toggleSelection = (index: number) => {
        if (hasEnriched && showEnrichedTable) {
            setEnrichedDifferences(prev => prev.map((d, i) =>
                i === index ? { ...d, selected: !d.selected } : d
            ));
        } else {
            setDifferences(prev => prev.map((d, i) =>
                i === index ? { ...d, selected: !d.selected } : d
            ));
        }
    };

    const handleFieldEdit = (
        index: number,
        field: keyof ScreenshotDifferenceWithJsonField | keyof ScreenshotDifference,
        value: string | boolean
    ) => {
        const safeValue = typeof value === 'string' ? value : value;
        if (hasEnriched && showEnrichedTable) {
            setEnrichedDifferences(prev => prev.map((d, i) =>
                i === index ? { ...d, [field]: safeValue } : d
            ));
        } else {
            setDifferences(prev => prev.map((d, i) =>
                i === index ? { ...d, [field]: safeValue } : d
            ));
        }
    };

    const handleJsonEdit = (value: string) => {
        try {
            const parsed = JSON.parse(value);
            if (Array.isArray(parsed)) {
                const withSelection = parsed.map(item => ({
                    ...item,
                    istWert: comparisonMode === 'SOLL_ONLY' ? undefined : sanitizeString(item.istWert),
                    sollWert: sanitizeString(item.sollWert),
                    comment: item.comment || '',
                    selected: item.selected !== undefined ? item.selected : true,
                    hidden: item.hidden !== undefined ? item.hidden : false
                }));

                if (hasEnriched && showEnrichedTable) {
                    setEnrichedDifferences(withSelection);
                } else {
                    setDifferences(withSelection);
                }
                setError('');
            }
        } catch (err) {
        }
    };

    const selectAll = () => {
        if (hasEnriched && showEnrichedTable) {
            setEnrichedDifferences(prev => prev.map(d => ({ ...d, selected: true })));
        } else {
            setDifferences(prev => prev.map(d => ({ ...d, selected: true })));
        }
    };

    const clearAll = () => {
        if (hasEnriched && showEnrichedTable) {
            setEnrichedDifferences(prev => prev.map(d => ({ ...d, selected: false })));
        } else {
            setDifferences(prev => prev.map(d => ({ ...d, selected: false })));
        }
    };

    const renderImageBox = (
        image: { file: File; preview: string } | null,
        type: 'ist' | 'soll',
        inputRef: React.RefObject<HTMLInputElement>,
        onSelect: (file: File) => void,
        zoom: number,
        setZoom: React.Dispatch<React.SetStateAction<number>>,
        isDragging: boolean
    ) => (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            <div style={{
                marginBottom: '6px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
            }}>
                <Label size="large" color="grey">{type.toUpperCase()}</Label>
                <div style={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
                    <Button.Group size="mini">
                        <Button
                            icon="minus"
                            onClick={() => setZoom(Math.max(0.1, zoom - 0.25))}
                            disabled={!image}
                        />
                        <Button disabled style={{ minWidth: '60px' }}>{Math.round(zoom * 100)}%</Button>
                        <Button
                            icon="plus"
                            onClick={() => setZoom(Math.min(5, zoom + 0.25))}
                            disabled={!image}
                        />
                    </Button.Group>
                    <Button
                        size="small"
                        icon
                        labelPosition="left"
                        onClick={() => inputRef.current?.click()}
                        color="grey"
                    >
                        <Icon name="upload" />
                        Upload
                    </Button>
                </div>
                <input
                    ref={inputRef}
                    type="file"
                    accept="image/jpeg,image/png"
                    style={{ display: 'none' }}
                    onChange={(e) => {
                        if (e.target.files?.[0]) {
                            onSelect(e.target.files[0]);
                        }
                        e.target.value = '';
                    }}
                />
            </div>
            <div
                style={{
                    border: isDragging ? '3px dashed #2185d0' : '2px dashed #ccc',
                    borderRadius: '4px',
                    height: '450px',
                    backgroundColor: isDragging ? '#e8f4fc' : '#f5f5f5',
                    position: 'relative',
                    transition: 'all 0.2s ease',
                    overflow: 'auto',
                    display: 'block'
                }}
                onDrop={(e) => handleDrop(e, type)}
                onDragOver={(e) => handleDragOver(e, type)}
                onDragLeave={(e) => handleDragLeave(e, type)}
                onPaste={(e) => handlePaste(e, type)}
                tabIndex={0}
            >
                {image ? (
                    <img
                        src={image.preview}
                        alt={type}
                        style={{
                            width: `${zoom * 100}%`,
                            maxWidth: 'none',
                            height: 'auto',
                            display: 'block',
                            margin: zoom <= 1 ? '0 auto' : '0'
                        }}
                    />
                ) : (
                    <div style={{
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexDirection: 'column',
                        textAlign: 'center',
                        color: '#888'
                    }}>
                        <Icon name="image outline" size="huge" />
                        <div style={{ marginTop: '10px' }}>
                            Drag & drop, paste, or click Upload
                        </div>
                    </div>
                )}
            </div>
        </div>
    );

    const renderTableView = (heightOverride?: string) => {
        const isEnriched = hasEnriched && showEnrichedTable;
        const isSollOnly = comparisonMode === 'SOLL_ONLY';

        return (
            <div style={{ maxHeight: heightOverride || (isTableExpanded ? '600px' : '350px'), overflowY: 'auto' }}>
                <Table celled striped compact size="small" fixed>
                    <Table.Header>
                        <Table.Row>
                            <Table.HeaderCell width={1} textAlign="center">Sel</Table.HeaderCell>
                            <Table.HeaderCell width={isEnriched ? 3 : 4}>Field Name</Table.HeaderCell>
                            {isEnriched && (
                                <Table.HeaderCell width={3}>JSON Field Path</Table.HeaderCell>
                            )}
                            {!isSollOnly && (
                                <Table.HeaderCell width={isEnriched ? 4 : 5}>IST Value</Table.HeaderCell>
                            )}
                            <Table.HeaderCell width={isEnriched ? 4 : 5}>SOLL Value</Table.HeaderCell>
                            <Table.HeaderCell width={isEnriched ? 3 : 4}>Comment</Table.HeaderCell>
                            <Table.HeaderCell width={1} textAlign="center">Client</Table.HeaderCell>
                        </Table.Row>
                    </Table.Header>
                    <Table.Body>
                        {visibleData.map((diff, idx) => {
                            const isMatch = !isSollOnly && areValuesEqual(diff.istWert, diff.sollWert);
                            const originalIndex = (isEnriched ? enrichedDifferences : differences)
                                .findIndex(d => d.feldname === diff.feldname && d.sollWert === diff.sollWert && d.istWert === diff.istWert);

                            return (
                                <Table.Row
                                    key={`${idx}-${diff.selected}`}
                                    style={{
                                        opacity: diff.selected ? 1 : 0.4,
                                        backgroundColor: isMatch ? '#e8f5e9' : undefined
                                    }}
                                >
                                    <Table.Cell textAlign="center">
                                        <Checkbox
                                            checked={!!diff.selected}
                                            onChange={() => toggleSelection(originalIndex)}
                                        />
                                    </Table.Cell>
                                    <Table.Cell>
                                        <Input
                                            transparent
                                            value={diff.feldname}
                                            onChange={(e) => handleFieldEdit(originalIndex, 'feldname', e.target.value)}
                                            style={{ width: '100%' }}
                                        />
                                    </Table.Cell>
                                    {isEnriched && (
                                        <Table.Cell style={{ wordBreak: 'break-all' }}>
                                            {isEnrichedDifference(diff) ? (
                                                diff.fieldNameInTransportOrderJson ? (
                                                    <Label size="tiny" color="blue">
                                                        <Icon name="code" />
                                                        {diff.fieldNameInTransportOrderJson}
                                                    </Label>
                                                ) : (
                                                    <Label size="tiny" color="grey">
                                                        <Icon name="question" />
                                                        Not mapped
                                                    </Label>
                                                )
                                            ) : (
                                                <Label size="tiny" color="grey">
                                                    <Icon name="question" />
                                                    Not mapped
                                                </Label>
                                            )}
                                        </Table.Cell>
                                    )}
                                    {!isSollOnly && (
                                        <Table.Cell>
                                            <Input
                                                transparent
                                                value={String(diff.istWert || '')}
                                                onChange={(e) => handleFieldEdit(originalIndex, 'istWert', e.target.value)}
                                                style={{ width: '100%' }}
                                            />
                                        </Table.Cell>
                                    )}
                                    <Table.Cell>
                                        <Input
                                            transparent
                                            value={String(diff.sollWert || '')}
                                            onChange={(e) => handleFieldEdit(originalIndex, 'sollWert', e.target.value)}
                                            style={{ width: '100%' }}
                                        />
                                    </Table.Cell>
                                    <Table.Cell>
                                        <Input
                                            transparent
                                            placeholder="Write a comment..."
                                            value={String(diff.comment || '')}
                                            onChange={(e) => handleFieldEdit(originalIndex, 'comment', e.target.value)}
                                            style={{ width: '100%' }}
                                        />
                                    </Table.Cell>
                                    <Table.Cell textAlign="center">
                                        <Checkbox
                                            checked={diff.isFeldMentionedByClient}
                                            onChange={(_, data) => handleFieldEdit(
                                                originalIndex,
                                                'isFeldMentionedByClient',
                                                data.checked || false
                                            )}
                                        />
                                    </Table.Cell>
                                </Table.Row>
                            );
                        })}
                    </Table.Body>
                </Table>
            </div>
        );
    };

    const renderJsonView = (heightOverride?: string) => {
        const viewData = selectedData.map(d => {
            const copy: any = { ...d };
            if (comparisonMode === 'SOLL_ONLY') {
                delete copy.istWert;
            }
            if (!copy.comment) copy.comment = "";
            return copy;
        });

        const jsonString = JSON.stringify(viewData, null, 2);

        const lineCount = jsonString.split('\n').length;
        const calculatedHeight = isTableExpanded
            ? Math.min(Math.max(lineCount * 20, 150), 600)
            : Math.min(Math.max(lineCount * 20, 150), 350);

        const finalHeight = heightOverride || `${calculatedHeight}px`;

        return (
            <div style={{ height: finalHeight, border: '1px solid #ccc', borderRadius: '4px', overflow: 'hidden' }}>
                <AceEditor
                    mode="json"
                    theme="monokai"
                    value={jsonString}
                    onChange={handleJsonEdit}
                    name="json_editor"
                    width="100%"
                    height="100%"
                    fontSize={13}
                    showPrintMargin={false}
                    setOptions={{
                        useWorker: false,
                        showLineNumbers: true,
                        tabSize: 2,
                    }}
                />
            </div>
        );
    };

    const renderTransportOrderSection = () => {
        const hasMultipleOrders = transportOrderIds.length > 1;
        const hasNoOrders = transportOrderIds.length === 0;

        return (
            <div style={{ marginBottom: '10px' }}>
                <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: '4px',
                    fontWeight: 'bold',
                    fontSize: '11px',
                }}>
                    <span>Transport Order</span>
                </label>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {transportOrderIds.length > 0 && (
                        <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                            {transportOrderIds.map((id) => (
                                <Label
                                    key={id}
                                    color={transportOrderIds.length === 1 ? 'blue' : 'orange'}
                                    style={{ cursor: 'default' }}
                                >
                                    <Icon name="file alternate outline" />
                                    <span>To:{id}</span>
                                    <Icon
                                        name='delete'
                                        onClick={(e: React.MouseEvent) => {
                                            e.stopPropagation();
                                            const newIds = transportOrderIds.filter(orderId => orderId !== id);
                                            onTransportOrderIdsChange(newIds);
                                        }}
                                        style={{ cursor: 'pointer', marginLeft: '5px' }}
                                    />
                                </Label>
                            ))}
                        </div>
                    )}

                    <Button
                        size="small"
                        icon
                        labelPosition="left"
                        onClick={() => setTransportOrderSelectorOpen(true)}
                        disabled={isLoadingTransportOrder}
                        fluid
                        color="blue"
                    >
                        <Icon name="search" />
                        {hasNoOrders ? 'Select Transport Order' : 'Change Transport Order'}
                    </Button>

                    {hasMultipleOrders && (
                        <Message warning size="tiny" style={{ margin: 0, padding: '8px' }}>
                            <Icon name="warning sign" />
                            Please select only one transport order for enrichment
                        </Message>
                    )}

                    {isLoadingTransportOrder && (
                        <Segment style={{ padding: '10px' }}>
                            <Loader active inline='centered' size='small'>Loading...</Loader>
                        </Segment>
                    )}
                </div>
            </div>
        );
    };

    const renderTransportOrderConfig = () => {
        if (!showTransportOrderConfig || !transportOrderJson) return null;

        return (
            <Segment style={{
                padding: '10px',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                backgroundColor: '#f9f9f9'
            }}>
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '10px'
                }}>
                    <Header as="h4" style={{ margin: 0 }}>
                        Transport Order JSON
                    </Header>
                    <Icon
                        name="compress"
                        style={{ cursor: 'pointer' }}
                        onClick={() => setShowTransportOrderConfig(false)}
                    />
                </div>

                <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                    <TransportOrderJsonViewer
                        transportOrder={transportOrderJson}
                        readOnly={false}
                        onChange={handleOptimizedJsonChange}
                    />
                </div>
            </Segment>
        );
    };

    const renderEnrichButton = () => {
        if (!response || !response.differences || response.differences.length === 0) {
            return null;
        }

        const canEnrich = transportOrderIds.length === 1 && optimizedTransportOrderJson;
        const hasMultipleOrders = transportOrderIds.length > 1;

        return (
            <Button
                color="grey"
                size="small"
                icon
                labelPosition="left"
                onClick={handleEnrichWithJsonMapping}
                loading={isEnriching}
                disabled={isEnriching || !canEnrich}
                title={
                    hasMultipleOrders
                        ? 'Please select only one transport order'
                        : !optimizedTransportOrderJson
                            ? 'Please select a transport order'
                            : ''
                }
            >
                <Icon name="magic" />
                {hasEnriched ? 'Re-enrich with JSON' : 'Enrich with JSON Fields'}
            </Button>
        );
    };

    const renderComparisonStats = () => {
        if (!response) return null;
        return (
            <Segment style={{ padding: '10px', backgroundColor: '#f9f9f9', margin: 0, height: '100%' }}>
                <Header as="h5" style={{ marginTop: 0, marginBottom: '8px' }}>
                    Comparison Stats
                </Header>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    {response.databaseEntryId && (
                        <Label size="small" color="grey" style={{ wordBreak: 'break-all' }}>
                            <Icon name="database" />
                            {response.databaseEntryId}
                        </Label>
                    )}
                    <Label size="small" color="grey">
                        <Icon name="list" />
                        {differences.length} differences
                    </Label>
                    <Label size="small" color="grey">
                        <Icon name="dollar" />
                        ${response.totalPrice.toFixed(6)}
                    </Label>
                    <Label size="small" color="grey">
                        <Icon name="file" />
                        {response.totalUsageTokensNumber} tokens
                    </Label>
                </div>
            </Segment>
        );
    }

    const renderEnrichmentStats = () => {
        if (!enrichmentResponse) return null;

        return (
            <Segment style={{ padding: '10px', backgroundColor: '#f9f9f9', margin: 0, height: '100%' }}>
                <Header as="h5" style={{ marginTop: 0, marginBottom: '8px' }}>
                    Enrichment Stats
                </Header>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <Label size="small" color="green">
                        <Icon name="check" />
                        {mappedCount} mapped
                    </Label>
                    {unmappedCount > 0 && (
                        <Label size="small" color="orange">
                            <Icon name="question" />
                            {unmappedCount} unmapped
                        </Label>
                    )}
                    <Label size="small" color="grey">
                        <Icon name="dollar" />
                        ${enrichmentResponse.totalPrice.toFixed(6)}
                    </Label>
                    <Label size="small" color="grey">
                        <Icon name="file" />
                        {enrichmentResponse.totalUsageTokensNumber} tokens
                    </Label>
                </div>
            </Segment>
        );
    };

    const isConfigVisible = showTransportOrderConfig && !!transportOrderJson;
    const leftColumnWidth = isConfigVisible ? 7 : 11;
    const rightColumnWidth = isConfigVisible ? 4 : 5;
    const configColumnWidth = 5;

    return (
        <Modal
            open={isOpen}
            onClose={onClose}
            size="fullscreen"
            style={{ width: '95vw' }}
        >
            <Modal.Header style={{ backgroundColor: '#666', color: 'white' }}>
                <Icon name="images" />
                Compare Screenshots
            </Modal.Header>

            <Modal.Content
                scrolling={hasResults}
                style={hasResults ? { maxHeight: 'calc(90vh - 180px)' } : undefined}
            >
                {(isProcessing || isEnriching) && (
                    <Dimmer active inverted>
                        <Loader>
                            {isProcessing ? 'Comparing Screenshots...' : 'Enriching with JSON Fields...'}
                        </Loader>
                    </Dimmer>
                )}

                {error && (
                    <Message negative onDismiss={() => setError('')}>
                        {error}
                    </Message>
                )}

                <Grid style={{ margin: 0 }}>
                    {!isTableExpanded && (
                        <Grid.Row style={{ paddingTop: '4px', paddingBottom: '4px' }}>
                            {/* Left - Images */}
                            <Grid.Column width={leftColumnWidth} style={{ paddingRight: '8px' }}>
                                <Segment style={{ padding: '4px', backgroundColor: '#fafafa', height: '100%' }}>
                                    <div style={{
                                        marginBottom: '8px',
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center'
                                    }}>
                                        <Header as="h3" style={{ margin: 0 }}>Screenshots</Header>
                                        <div>
                                            {comparisonMode === 'IST_VS_SOLL' && (
                                                <>
                                                    <Button
                                                        icon
                                                        labelPosition="left"
                                                        size="small"
                                                        onClick={() => bothInputRef.current?.click()}
                                                        color="blue"
                                                    >
                                                        <Icon name="upload" />
                                                        Upload Both
                                                    </Button>
                                                    <Button
                                                        icon
                                                        labelPosition="left"
                                                        size="small"
                                                        onClick={handleInvert}
                                                        disabled={!istImage || !sollImage || isProcessing || hasResults}
                                                        style={{ marginLeft: '8px' }}
                                                        color="black"
                                                    >
                                                        <Icon name="exchange" />
                                                        Invert
                                                    </Button>
                                                    <input
                                                        ref={bothInputRef}
                                                        type="file"
                                                        accept="image/jpeg,image/png"
                                                        multiple
                                                        style={{ display: 'none' }}
                                                        onChange={(e) => e.target.files && handleBothFilesSelect(e.target.files)}
                                                    />
                                                </>
                                            )}
                                        </div>
                                    </div>

                                    <div style={{ display: 'flex', gap: '10px', height: '100%' }}>
                                        {comparisonMode === 'IST_VS_SOLL' && renderImageBox(
                                            istImage,
                                            'ist',
                                            istInputRef,
                                            (file) => handleSingleFileSelect(file, 'ist'),
                                            istZoom,
                                            setIstZoom,
                                            isDraggingIst
                                        )}
                                        {renderImageBox(
                                            sollImage,
                                            'soll',
                                            sollInputRef,
                                            (file) => handleSingleFileSelect(file, 'soll'),
                                            sollZoom,
                                            setSollZoom,
                                            isDraggingSoll
                                        )}
                                    </div>
                                </Segment>
                            </Grid.Column>

                            {isConfigVisible && (
                                <Grid.Column
                                    width={4}
                                    style={{ paddingLeft: '4px', paddingRight: '4px' }}
                                >
                                    {renderTransportOrderConfig()}
                                </Grid.Column>
                            )}

                            <Grid.Column width={configColumnWidth} style={{ paddingLeft: '8px', display: 'flex', flexDirection: 'column' }}>
                                <Segment style={{ padding: '8px', backgroundColor: '#fafafa', marginBottom: '8px' }}>
                                    <Header as="h4" style={{ marginTop: 0, marginBottom: '10px' }}>
                                        Configuration
                                    </Header>

                                    <div style={{ marginBottom: '10px' }}>
                                        <label style={{
                                            display: 'block',
                                            marginBottom: '4px',
                                            fontWeight: 'bold',
                                            fontSize: '11px'
                                        }}>
                                            Comparison Mode
                                        </label>
                                        <Dropdown
                                            fluid
                                            selection
                                            options={COMPARISON_MODES}
                                            value={comparisonMode}
                                            onChange={(_, data) => setComparisonMode(data.value as ScreenshotComparisonMode)}
                                            disabled={hasResults}
                                        />
                                    </div>

                                    <div style={{ marginBottom: '10px' }}>
                                        <label style={{
                                            display: 'block',
                                            marginBottom: '4px',
                                            fontWeight: 'bold',
                                            fontSize: '11px'
                                        }}>
                                            Image Processing Mode
                                        </label>
                                        <Dropdown
                                            fluid
                                            selection
                                            options={IMAGE_PROCESSING_MODES}
                                            value={imageProcessingMode}
                                            onChange={(_, data) => setImageProcessingMode(data.value as ImageProcessingMode)}
                                        />
                                    </div>

                                    <div style={{ marginBottom: '10px' }}>
                                        <label style={{
                                            display: 'block',
                                            marginBottom: '4px',
                                            fontWeight: 'bold',
                                            fontSize: '11px'
                                        }}>
                                            AI Model
                                        </label>
                                        <Dropdown
                                            fluid
                                            selection
                                            options={ALLOWED_MODELS}
                                            value={selectedModel}
                                            onChange={(_, data) => setSelectedModel(data.value as string)}
                                        />
                                    </div>

                                    <div style={{ marginBottom: '10px' }}>
                                        <label style={{
                                            display: 'block',
                                            marginBottom: '4px',
                                            fontWeight: 'bold',
                                            fontSize: '11px'
                                        }}>
                                            Custom Prompt (Optional)
                                        </label>
                                        <textarea
                                            placeholder="e.g., Focus on invoice fields..."
                                            value={customPrompt}
                                            onChange={(e) => setCustomPrompt(e.target.value)}
                                            style={{
                                                width: '100%',
                                                height: '60px',
                                                resize: 'none',
                                                fontSize: '13px',
                                                fontFamily: 'monospace',
                                                padding: '8px',
                                                border: '1px solid #ddd',
                                                borderRadius: '4px'
                                            }}
                                        />
                                    </div>

                                    {renderTransportOrderSection()}

                                    {transportOrderIds.length === 1 && (
                                        <Button
                                            size="small"
                                            icon
                                            labelPosition="left"
                                            onClick={() => setShowTransportOrderConfig(!showTransportOrderConfig)}
                                            style={{ marginBottom: '10px' }}
                                            fluid
                                            color="grey"
                                        >
                                            <Icon name={showTransportOrderConfig ? 'compress' : 'expand'} />
                                            {showTransportOrderConfig ? 'Hide' : 'Show'} JSON Settings
                                        </Button>
                                    )}

                                    <Button
                                        fluid
                                        icon
                                        labelPosition="left"
                                        onClick={handleCompare}
                                        disabled={
                                            isProcessing ||
                                            (comparisonMode === 'IST_VS_SOLL' ? (!istImage || !sollImage) : !sollImage)
                                        }
                                        size="large"
                                        color="black"
                                    >
                                        <Icon name="search" />
                                        {comparisonMode === 'IST_VS_SOLL' ? 'Compare Screenshots' : 'Analyze Screenshot'}
                                    </Button>
                                </Segment>

                                {(response || enrichmentResponse) && (
                                    <div style={{ display: 'flex', gap: '8px', flex: 1 }}>
                                        {response && (
                                            <div style={{ flex: 1 }}>
                                                {renderComparisonStats()}
                                            </div>
                                        )}
                                        {enrichmentResponse && (
                                            <div style={{ flex: 1 }}>
                                                {renderEnrichmentStats()}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </Grid.Column>
                        </Grid.Row>
                    )}

                    {hasResults && (
                        <Grid.Row style={{ paddingTop: '8px', paddingBottom: '8px' }}>
                            <Grid.Column width={16}>
                                <Segment style={{ padding: '10px', backgroundColor: '#fafafa' }}>
                                    <div style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        marginBottom: '8px',
                                        alignItems: 'center',
                                        flexWrap: 'wrap',
                                        gap: '8px'
                                    }}>
                                        <Header as="h3" style={{ margin: 0 }}>
                                            Selected: {selectedCount} / Visible: {visibleData.length}
                                            {hasEnriched && showEnrichedTable && (
                                                <Label color="red" style={{ marginLeft: '10px' }}>
                                                    Enriched
                                                </Label>
                                            )}
                                        </Header>

                                        <div style={{
                                            display: 'flex',
                                            gap: '8px',
                                            alignItems: 'center',
                                            flexWrap: 'wrap'
                                        }}>
                                            {matchingFieldsCount > 0 && (
                                                <Checkbox
                                                    label={comparisonMode === 'SOLL_ONLY'
                                                        ? `Hide empty soll values (${matchingFieldsCount})`
                                                        : `Hide matching (${matchingFieldsCount})`}
                                                    checked={hideMatchingFields}
                                                    onChange={(_, data) =>
                                                        setHideMatchingFields(data.checked || false)}
                                                />
                                            )}

                                            {hasEnriched && showEnrichedTable && unmappedCount > 0 && (
                                                <Checkbox
                                                    label={`Hide unmapped (${unmappedCount})`}
                                                    checked={hideUnmappedFields}
                                                    onChange={(_, data) =>
                                                        setHideUnmappedFields(data.checked || false)}
                                                />
                                            )}

                                            {hasEnriched && (
                                                <Button
                                                    size="small"
                                                    icon
                                                    labelPosition="left"
                                                    onClick={() => setShowEnrichedTable(!showEnrichedTable)}
                                                    color={showEnrichedTable ? 'blue' : undefined}
                                                >
                                                    <Icon name="exchange" />
                                                    {showEnrichedTable ? 'Show Original' : 'Show Enriched'}
                                                </Button>
                                            )}

                                            {renderEnrichButton()}

                                            <Button
                                                size="small"
                                                icon
                                                labelPosition="left"
                                                onClick={() => setIsTableExpanded(!isTableExpanded)}
                                                color="grey"
                                            >
                                                <Icon name={isTableExpanded ? 'compress' : 'expand'} />
                                                {isTableExpanded ? 'Collapse' : 'Expand'}
                                            </Button>

                                            <Menu compact size="small">
                                                <Menu.Item
                                                    active={viewMode === 'table'}
                                                    onClick={() => setViewMode('table')}
                                                >
                                                    <Icon name="table" />
                                                    Table
                                                </Menu.Item>
                                                <Menu.Item
                                                    active={viewMode === 'split'}
                                                    onClick={() => setViewMode('split')}
                                                >
                                                    <Icon name="columns" />
                                                    Split
                                                </Menu.Item>
                                                <Menu.Item
                                                    active={viewMode === 'json'}
                                                    onClick={() => setViewMode('json')}
                                                >
                                                    <Icon name="code" />
                                                    JSON
                                                </Menu.Item>
                                            </Menu>

                                            <Label
                                                size="small"
                                                color={selectedCount > 0 ? 'green' : 'grey'}
                                            >
                                                {selectedCount} selected
                                            </Label>

                                            <Button size="small" onClick={selectAll} color="grey">
                                                Select All
                                            </Button>

                                            <Button size="small" onClick={clearAll} color="grey">
                                                Clear All
                                            </Button>
                                        </div>
                                    </div>

                                    {viewMode === 'table' && renderTableView()}
                                    {viewMode === 'json' && renderJsonView()}
                                    {viewMode === 'split' && (
                                        <Grid>
                                            <Grid.Column width={8}>
                                                {renderTableView('500px')}
                                            </Grid.Column>
                                            <Grid.Column width={8}>
                                                {renderJsonView('500px')}
                                            </Grid.Column>
                                        </Grid>
                                    )}
                                </Segment>
                            </Grid.Column>
                        </Grid.Row>
                    )}
                </Grid>
            </Modal.Content>

            <Modal.Actions style={{ backgroundColor: '#f0f0f0' }}>
                <Button onClick={onClose} color="red">
                    Cancel
                </Button>
                {hasResults && (
                    <>
                        <Button
                            icon
                            labelPosition="left"
                            onClick={() => handleApplyToPrompt('raw')}
                            disabled={selectedCount === 0}
                            color="blue"
                        >
                            <Icon name="plus" />
                            Add as Raw ({selectedCount})
                        </Button>
                        <Button
                            icon
                            labelPosition="left"
                            onClick={() => handleApplyToPrompt('json')}
                            disabled={selectedCount === 0}
                            color="blue"
                        >
                            <Icon name="code" />
                            Add as JSON ({selectedCount})
                        </Button>
                    </>
                )}
            </Modal.Actions>

            {transportOrderSelectorOpen && (
                <TransportOrderSelector
                    isOpen={transportOrderSelectorOpen}
                    onClose={() => setTransportOrderSelectorOpen(false)}
                    transportOrderIds={transportOrderIds}
                    setTransportOrderIds={onTransportOrderIdsChange}
                    allowMultipleSelection={false}
                />
            )}
        </Modal>
    );
}